import React, { useEffect, useMemo, useState } from "react";
import Select, { MultiValue } from "react-select";
import makeAnimated from "react-select/animated";
import { toast } from "sonner";
import { useAppSelector } from "@src/store/hooks";
import { CopyIcon } from "@src/assets/general-icons";
import { EventInventoryRecord, selectSelectedInventoryItems } from "@src/store/pricing/queryEventInventoryAllSlice";
import { ApiProvider } from "@src/store/ApiProvider";
import { Theme } from "@src/store/settingsSlice";
import helperStyles from "@src/components/helpers.module.scss";
import { Button, ButtonVariant } from "../button/Button";
import EventContextHeader from "@src/components/eventContextHeader/EventContextHeader";
import { Switch } from "../switch/Switch";
import { Loader } from "../loader/Loader";
import { clx } from "../utils/stringUtils";
import BulkInternalNotesItem from "./BulkInternalNotesItem";
import styles from "./BulkInternalNotes.module.scss";

type TBulkInternalNotes = {
	handleClose?: () => void;
};

type SelectOption<T> = { label: string; value: T };

interface VendorParent {
	vendorParentId: string;
	name: string;
	vendorId?: string;
}

interface Vendor {
	vendorId: string;
	name: string;
	url: string;
	vendorParentID: string;
}

type EventSiteOption = SelectOption<VendorParent>;
type VendorOption = SelectOption<Vendor>;

interface MaskedDetails {
	section: string;
	row: string;
	seat: string;
}

interface InternalNoteData {
	orderId: string;
	email: string;
	password: string;
	eventSite: string;
	transferlessLink: string;
	comments: string;
	vendorId: string | null;
	vendorParentId: string | null;
	maskedDetails?: MaskedDetails;
}

interface NoteItem {
	seat: string;
	itemId: number;
	itemStatusId: number;
	listingId: number;
	internalNote: InternalNoteData;
}

const FLAG_TOKENS = [
	{ key: "noPasswordManager" as const, abbr: "-NoPasswordManager", label: "No Password Manager" },
	{ key: "anonymousTransfer" as const, abbr: "-AnonymousTransfer", label: "Anonymous Transfer" },
];

const abbreviatedEventSites = [
	{ name: "All", abbr: "-all" },
	{ name: "FanXchange", abbr: "-fanx" },
	{ name: "Gametime", abbr: "-gt" },
	{ name: "GoTickets", abbr: "-gotix" },
	{ name: "SeatGeek", abbr: "-sg" },
	{ name: "Ticket Evolution", abbr: "-tevo" },
	{ name: "TicketNetwork", abbr: "-tnd" },
	{ name: "TicketsNow", abbr: "-tnow" },
	{ name: "TM+", abbr: "-tm+" },
	{ name: "TickPick", abbr: "-tpick" },
	{ name: "Viagogo / Stubhub", abbr: "-Vgg" },
	{ name: "Vivid Seats", abbr: "-vivid" },
];

const getCompactSelectStyles = (theme: Theme) => {
	const isLight = theme === Theme.Light;
	const bg = isLight ? "#fafafa" : "#0d0d0d";
	const bgSelected = isLight ? "#EDEEF4" : "#2f2f2f";
	const border = isLight ? "#ddddda" : "#2f2f2f";
	const textColor = isLight ? "#212121" : "#fafafa";
	const placeholderColor = isLight ? "#888888" : "#a3a3a3";
	const hoverBorder = isLight ? "#888888" : "#a3a3a3";

	return {
		control: (base: Record<string, unknown>) => ({
			...base,
			minHeight: "38px",
			fontSize: "1.3rem",
			borderRadius: "6px",
			background: bg,
			border: `1px solid ${border}`,
			boxShadow: "none",
			"&:hover": { borderColor: hoverBorder },
		}),
		menu: (base: Record<string, unknown>) => ({
			...base,
			fontSize: "1.3rem",
			zIndex: 10,
			background: bg,
			border: `1px solid ${border}`,
		}),
		menuList: (base: Record<string, unknown>) => ({ ...base, maxHeight: "180px" }),
		valueContainer: (base: Record<string, unknown>) => ({ ...base, padding: "2px 8px" }),
		singleValue: (base: Record<string, unknown>) => ({ ...base, color: textColor }),
		input: (base: Record<string, unknown>) => ({ ...base, color: textColor }),
		option: (base: Record<string, unknown>, state: { isSelected: boolean }) => ({
			...base,
			fontSize: "1.3rem",
			padding: "8px 12px",
			backgroundColor: state.isSelected ? bgSelected : bg,
			color: textColor,
			"&:hover": { backgroundColor: bgSelected },
		}),
		placeholder: (base: Record<string, unknown>) => ({ ...base, color: placeholderColor }),
		multiValue: (base: Record<string, unknown>) => ({
			...base,
			backgroundColor: bgSelected,
			border: `1px solid ${border}`,
			borderRadius: "4px",
		}),
		multiValueLabel: (base: Record<string, unknown>) => ({ ...base, color: textColor, fontSize: "1.2rem" }),
		multiValueRemove: (base: Record<string, unknown>) => ({
			...base,
			color: textColor,
			"&:hover": { backgroundColor: hoverBorder, color: isLight ? "#fff" : "#000" },
		}),
	};
};

const isAbbrToken = (token: string): boolean =>
	abbreviatedEventSites.some((s) => s.abbr === token) || FLAG_TOKENS.some((f) => f.abbr === token);

const stripAbbrLines = (text: string): string => {
	return text
		.split("\n")
		.filter((line) => {
			const tokens = line.trim().split(/\s+/).filter(Boolean);
			if (tokens.length === 0) return true;
			return !tokens.every((t) => isAbbrToken(t));
		})
		.join("\n");
};

const rebuildComments = (
	base: string,
	exclusions: Array<{ value: string }>,
	flagState: { noPasswordManager: boolean; anonymousTransfer: boolean },
): string => {
	const baseContent = stripAbbrLines(base).replace(/\n+$/g, "");
	const exclusionLine = exclusions.map((o) => o.value).join(" ");
	const flagLine = FLAG_TOKENS.filter((f) => flagState[f.key])
		.map((f) => f.abbr)
		.join(" ");

	const parts = [baseContent, exclusionLine, flagLine].filter(Boolean);
	return parts.join("\n");
};

const allSame = <T,>(arr: Array<T>): T | undefined => {
	if (!arr.length) return undefined;
	const first = arr[0];
	return arr.every((x) => x === first) ? first : undefined;
};

let cachedVendorParents: Array<VendorParent> | null = null;
let cachedVendors: Array<Vendor> | null = null;

const BulkInternalNotes = ({ handleClose }: TBulkInternalNotes) => {
	const items = useAppSelector(selectSelectedInventoryItems);
	const theme = useAppSelector((state) => state.settings.theme);

	const [keptItems, setKeptItems] = useState<Array<EventInventoryRecord>>(items);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);
	const [noteData, setNoteData] = useState<Array<NoteItem>>([]);

	const [eventSites, setEventSites] = useState<Array<EventSiteOption>>([]);
	const [eventSiteDetails, setEventSiteDetails] = useState<Array<Vendor>>([]);
	const [filteredEventSites, setFilteredEventSites] = useState<Array<VendorOption>>([]);
	const [chosenEventSite, setChosenEventSite] = useState<EventSiteOption | null>(null);
	const [chosenEventSiteDetail, setChosenEventSiteDetail] = useState<VendorOption | null>(null);
	const [selectedVendorId, setSelectedVendorId] = useState<string | null>(null);
	const [linkUrl, setLinkUrl] = useState("");

	const [orderId, setOrderId] = useState("");
	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");
	const [transferlessLink, setTransferlessLink] = useState("");
	const [comments, setComments] = useState("");
	const [selectedExclusions, setSelectedExclusions] = useState<Array<SelectOption<string>>>([]);

	const [noPasswordManager, setNoPasswordManager] = useState(false);
	const [isMasked, setIsMasked] = useState(false);
	const [anonymousTransfer, setAnonymousTransfer] = useState(false);
	const [maskedSection, setMaskedSection] = useState("");
	const [maskedRow, setMaskedRow] = useState("");
	const [maskedSeat, setMaskedSeat] = useState("");

	const [dirty, setDirty] = useState({
		orderId: false,
		email: false,
		password: false,
		eventSite: false,
		transferlessLink: false,
		comments: false,
		flags: false,
	});

	const compactSelectStyles = useMemo(() => getCompactSelectStyles(theme), [theme]);

	useEffect(() => {
		setKeptItems(items);
	}, [items]);

	const handleRemoveItem = (listingId: number) => {
		setKeptItems((prev) => prev.filter((x) => x.Listing_ID !== listingId));
	};

	const fetchEventSites = async (): Promise<Array<VendorParent>> => {
		if (cachedVendorParents) return cachedVendorParents;
		const data = await ApiProvider.default.get<Array<VendorParent>>("/api/Vendor/vendorParents");
		cachedVendorParents = data;
		return data;
	};

	const fetchVendors = async (): Promise<Array<Vendor>> => {
		if (cachedVendors) return cachedVendors;
		const data = await ApiProvider.default.get<Array<Vendor>>("/api/Vendor/vendors");
		cachedVendors = data;
		return data;
	};

	const getEventSiteOptions = (vendorParentId: string, vendors: Array<Vendor>): Array<VendorOption> => {
		return vendors.filter((v) => v.vendorParentID === vendorParentId).map((v) => ({ label: v.name, value: v }));
	};

	useEffect(() => {
		const run = async () => {
			setLoading(true);
			try {
				const [parents, vendors] = await Promise.all([fetchEventSites(), fetchVendors()]);
				setEventSites(parents.map((p) => ({ label: p.name, value: p })));
				setEventSiteDetails(vendors);

				const listingIds = Array.from(new Set(items.map((x) => x.Listing_ID)));
				const results = await Promise.all(
					listingIds.map((id) =>
						ApiProvider.default
							.get<Array<NoteItem>>(`/api/internalnote/listingId/${id}`)
							.then((data) => data.map((n) => ({ ...n, listingId: id })))
							.catch(() => [] as Array<NoteItem>),
					),
				);
				const aggregated = results.flat();
				setNoteData(aggregated);

				// Pre-populate uniform values
				if (aggregated.length) {
					const uniqOrderId = allSame(aggregated.map((x) => x.internalNote.orderId ?? ""));
					if (uniqOrderId) setOrderId(uniqOrderId);

					const uniqEmail = allSame(aggregated.map((x) => x.internalNote.email ?? ""));
					if (uniqEmail) setEmail(uniqEmail);

					// TAC-2365: the password is intentionally not read from the API response (the backend
					// excludes it from serialization). The field stays write-only — blank unless the user
					// types a new value.

					const uniqLink = allSame(aggregated.map((x) => x.internalNote.transferlessLink ?? ""));
					if (uniqLink) setTransferlessLink(uniqLink);

					const uniqComments = allSame(aggregated.map((x) => x.internalNote.comments ?? ""));
					if (uniqComments) {
						setComments(uniqComments);
						const tokens = uniqComments.split(/\s+/).filter(Boolean);
						const initialExclusions = abbreviatedEventSites
							.filter((s) => tokens.includes(s.abbr))
							.map((s) => ({ value: s.abbr, label: s.name }));
						setSelectedExclusions(initialExclusions);
						const nopmToken = FLAG_TOKENS.find((f) => f.key === "noPasswordManager")?.abbr;
						const anonToken = FLAG_TOKENS.find((f) => f.key === "anonymousTransfer")?.abbr;
						if (nopmToken && tokens.includes(nopmToken)) setNoPasswordManager(true);
						if (anonToken && tokens.includes(anonToken)) setAnonymousTransfer(true);
					}

					const uniqEventSite = allSame(aggregated.map((x) => x.internalNote.eventSite ?? ""));
					if (uniqEventSite) {
						const found = parents.find((p) => p.name === uniqEventSite);
						if (found) {
							const site = { label: found.name, value: found };
							setChosenEventSite(site);
							const vendorId = aggregated[0].internalNote.vendorId;
							setSelectedVendorId(vendorId);
							if (!found.vendorId) {
								const opts = getEventSiteOptions(found.vendorParentId, vendors);
								setFilteredEventSites(opts);
								const detail = vendors.find((v) => v.vendorId === vendorId);
								if (detail) {
									setChosenEventSiteDetail({ label: detail.name, value: detail });
									setLinkUrl(detail.url ?? "");
								}
							}
						}
					}

					const allMasked = aggregated.every((x) => x.internalNote.maskedDetails);
					setIsMasked(allMasked);
					if (allMasked) {
						const uniqSection = allSame(aggregated.map((x) => x.internalNote.maskedDetails?.section ?? ""));
						const uniqRow = allSame(aggregated.map((x) => x.internalNote.maskedDetails?.row ?? ""));
						const uniqSeat = allSame(aggregated.map((x) => x.internalNote.maskedDetails?.seat ?? ""));
						if (uniqSection !== undefined) setMaskedSection(uniqSection);
						if (uniqRow !== undefined) setMaskedRow(uniqRow);
						if (uniqSeat !== undefined) setMaskedSeat(uniqSeat);
					}
				}
			} catch (err) {
				toast.error("Failed to load internal notes.");
				console.error(err);
			} finally {
				setLoading(false);
			}
		};
		run();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const markDirty = (field: keyof typeof dirty) => setDirty((prev) => (prev[field] ? prev : { ...prev, [field]: true }));

	const handleEventSiteChange = (selected: EventSiteOption | null) => {
		setChosenEventSite(selected);
		setSelectedVendorId(null);
		setChosenEventSiteDetail(null);
		setLinkUrl("");
		markDirty("eventSite");
		if (selected && !selected.value.vendorId) {
			setFilteredEventSites(getEventSiteOptions(selected.value.vendorParentId, eventSiteDetails));
		} else {
			setFilteredEventSites([]);
		}
	};

	const handleManagerChange = (selected: VendorOption | null) => {
		setSelectedVendorId(selected?.value.vendorId || null);
		setChosenEventSiteDetail(selected);
		setLinkUrl(selected?.value.url || "");
		markDirty("eventSite");
	};

	const handleExclusionChange = (selectedOptions: MultiValue<SelectOption<string>> | null): void => {
		const newSelected = selectedOptions ? Array.from(selectedOptions) : [];
		setSelectedExclusions(newSelected);
		setComments((prev) => rebuildComments(prev, newSelected, { noPasswordManager, anonymousTransfer }));
		markDirty("comments");
	};

	const handleFlagToggle = (key: "noPasswordManager" | "anonymousTransfer", value: boolean) => {
		const nextFlagState = {
			noPasswordManager: key === "noPasswordManager" ? value : noPasswordManager,
			anonymousTransfer: key === "anonymousTransfer" ? value : anonymousTransfer,
		};
		if (key === "noPasswordManager") setNoPasswordManager(value);
		if (key === "anonymousTransfer") setAnonymousTransfer(value);
		setComments((prev) => rebuildComments(prev, selectedExclusions, nextFlagState));
		markDirty("flags");
		markDirty("comments");
	};

	const handleSave = async () => {
		if (!keptItems.length || !noteData.length) return;

		const keptListingIdSet = new Set(keptItems.map((x) => x.Listing_ID));
		const itemsToSave = noteData.filter((n) => keptListingIdSet.has(n.listingId));
		if (!itemsToSave.length) {
			toast.error("No listings to save.");
			return;
		}

		setSaving(true);
		try {
			const requestData = itemsToSave.map((n) => {
				const existing = n.internalNote;

				const next: InternalNoteData = {
					orderId: dirty.orderId ? orderId : (existing.orderId ?? ""),
					email: dirty.email ? email : (existing.email ?? ""),
					password: dirty.password ? password : "", // TAC-2365: blank keeps the stored credential; never round-trip the existing password
					eventSite: dirty.eventSite ? (chosenEventSite?.label ?? "") : (existing.eventSite ?? ""),
					transferlessLink: dirty.transferlessLink ? transferlessLink : (existing.transferlessLink ?? ""),
					comments: dirty.comments ? comments : (existing.comments ?? ""),
					vendorId: dirty.eventSite
						? selectedVendorId || chosenEventSite?.value.vendorId || null
						: (existing.vendorId ?? null),
					vendorParentId: dirty.eventSite
						? (chosenEventSite?.value.vendorParentId ?? null)
						: (existing.vendorParentId ?? null),
				};

				if (dirty.flags) {
					if (isMasked) {
						next.maskedDetails = { section: maskedSection, row: maskedRow, seat: maskedSeat };
					}
				} else if (existing.maskedDetails) {
					next.maskedDetails = existing.maskedDetails;
				}

				return {
					itemId: n.itemId,
					user: "Admin",
					internalNote: next,
				};
			});

			await ApiProvider.default.post("/api/InternalNote/BulkUpsert", requestData);
			toast.success("Internal notes updated.");
			handleClose?.();
		} catch (err) {
			console.error(err);
			toast.error("Failed to update internal notes.");
		} finally {
			setSaving(false);
		}
	};

	// Summary panel computed values
	const summaryMarketplaces = useMemo(() => {
		const unique = Array.from(new Set(noteData.map((n) => n.internalNote.eventSite).filter(Boolean)));
		return unique;
	}, [noteData]);

	const fieldsToUpdate = useMemo(() => {
		const labels: Array<string> = [];
		if (dirty.orderId) labels.push("Order ID");
		if (dirty.email) labels.push("Email");
		if (dirty.password) labels.push("Password");
		if (dirty.eventSite) labels.push("Marketplace");
		if (dirty.transferlessLink) labels.push("Wallet Link");
		if (dirty.comments) labels.push("Comments");
		return labels;
	}, [dirty]);

	const flagsEnabled = [
		noPasswordManager && "No Password Manager",
		isMasked && "Is Masked",
		anonymousTransfer && "Anonymous Transfer",
	].filter(Boolean) as Array<string>;

	const flagsDisabled = [
		!noPasswordManager && "No Password Manager",
		!isMasked && "Is Masked",
		!anonymousTransfer && "Anonymous Transfer",
	].filter(Boolean) as Array<string>;

	const mappedItems = keptItems.map((x) => <BulkInternalNotesItem key={x.Item_ID} data={x} onRemove={handleRemoveItem} />);

	if (loading) {
		return (
			<div className={styles.root}>
				<div className={styles.loaderWrapper}>
					<Loader controlSize="l" />
				</div>
			</div>
		);
	}

	return (
		<div className={styles.root}>
			<EventContextHeader padding listings={items.map((x) => ({ section: x.Section, row: x.Row, seats: x.Seats }))} />
			<div className={styles.top}>
				<div className={styles.left}>
					<div className={styles.section}>
						<div className={styles.subHeader}>Order &amp; Fulfillment Details</div>
						<div className={styles.fieldsGrid}>
							<div className={styles.field}>
								<div className={styles.label}>Order ID</div>
								<input
									className={styles.input}
									type="text"
									placeholder="e.g. 12345678"
									value={orderId}
									onChange={(e) => {
										setOrderId(e.target.value);
										markDirty("orderId");
									}}
								/>
							</div>
							<div className={styles.field}>
								<div className={styles.label}>Email</div>
								<input
									className={styles.input}
									type="text"
									placeholder="example@domain.com"
									value={email}
									onChange={(e) => {
										setEmail(e.target.value);
										markDirty("email");
									}}
								/>
							</div>
							<div className={styles.field}>
								<div className={styles.label}>Password</div>
								<input
									className={styles.input}
									type="text"
									placeholder="Enter new password"
									value={password}
									onChange={(e) => {
										setPassword(e.target.value);
										markDirty("password");
									}}
								/>
							</div>
							<div className={styles.field}>
								<div className={styles.label}>Marketplace</div>
								<Select
									options={eventSites}
									value={chosenEventSite}
									onChange={handleEventSiteChange}
									components={makeAnimated()}
									closeMenuOnSelect
									isMulti={false}
									isSearchable
									placeholder="Choose event site"
									styles={compactSelectStyles}
								/>
							</div>
							{chosenEventSite && !chosenEventSite.value.vendorId && (
								<>
									<div className={styles.field}>
										<div className={styles.label}>Account Manager</div>
										<Select
											options={filteredEventSites}
											value={chosenEventSiteDetail}
											onChange={handleManagerChange}
											closeMenuOnSelect
											isMulti={false}
											styles={compactSelectStyles}
										/>
									</div>
									<div className={styles.field}>
										<div className={styles.label}>Link</div>
										<div className={styles.inputWithAction}>
											<input
												className={styles.input}
												type="text"
												placeholder="Link"
												value={linkUrl}
												onChange={(e) => setLinkUrl(e.target.value)}
											/>
											<button
												type="button"
												className={styles.copyButton}
												aria-label="Copy link"
												disabled={!linkUrl}
												onClick={() => navigator.clipboard.writeText(linkUrl)}>
												<CopyIcon />
											</button>
										</div>
									</div>
								</>
							)}
							<div className={clx(styles.field, styles.fieldFull)}>
								<div className={styles.label}>Exclusion list</div>
								<Select
									options={abbreviatedEventSites.map((s) => ({ value: s.abbr, label: s.name }))}
									value={selectedExclusions}
									isMulti
									closeMenuOnSelect={false}
									onChange={handleExclusionChange}
									placeholder="Choose marketplace"
									styles={compactSelectStyles}
								/>
							</div>
						</div>
					</div>

					<div className={styles.section}>
						<div className={styles.subHeader}>Transfer &amp; Visibility Flags</div>
						<div className={styles.description}>
							Configure delivery handling and marketplace visibility rules for the selected listings.
						</div>
						<div className={styles.toggles}>
							<div className={styles.toggle}>
								<Switch
									checked={noPasswordManager}
									onChange={(v) => handleFlagToggle("noPasswordManager", v)}
									label="No Password Manager"
								/>
							</div>
							<div className={styles.toggle}>
								<Switch
									checked={isMasked}
									onChange={(v) => {
										setIsMasked(v);
										markDirty("flags");
									}}
									tooltip="If you need to inform our team that the section, row, or seats are different from what is displayed, check the 'Is Masked' option. This helps our team accurately locate your tickets."
									tooltipId="bulk-is-masked"
									label="Is Masked"
								/>
							</div>
							<div className={styles.toggle}>
								<Switch
									checked={anonymousTransfer}
									onChange={(v) => handleFlagToggle("anonymousTransfer", v)}
									label="Anonymous Transfer"
								/>
							</div>
						</div>
						{isMasked ? (
							<div className={styles.maskedFieldsRow}>
								<div className={styles.field}>
									<div className={styles.label}>Masked Section</div>
									<input
										className={styles.input}
										type="text"
										placeholder="Section"
										value={maskedSection}
										onChange={(e) => {
											setMaskedSection(e.target.value);
											markDirty("flags");
										}}
									/>
								</div>
								<div className={styles.field}>
									<div className={styles.label}>Masked Row</div>
									<input
										className={styles.input}
										type="text"
										placeholder="Row"
										value={maskedRow}
										onChange={(e) => {
											setMaskedRow(e.target.value);
											markDirty("flags");
										}}
									/>
								</div>
								<div className={styles.field}>
									<div className={styles.label}>Masked Seat</div>
									<input
										className={styles.input}
										type="text"
										placeholder="Seat"
										value={maskedSeat}
										onChange={(e) => {
											setMaskedSeat(e.target.value);
											markDirty("flags");
										}}
									/>
								</div>
							</div>
						) : null}
					</div>

					<div className={styles.section}>
						<div className={styles.subHeader}>Wallet Link</div>
						<textarea
							className={styles.smallTextArea}
							placeholder="Input all Wallet Links that are required for transferless events."
							value={transferlessLink}
							onChange={(e) => {
								setTransferlessLink(e.target.value);
								markDirty("transferlessLink");
							}}
						/>
					</div>

					<div className={styles.section}>
						<div className={styles.subHeader}>Comments</div>
						<textarea
							className={styles.textArea}
							placeholder="Add internal notes for these listings"
							value={comments}
							onChange={(e) => {
								setComments(e.target.value);
								markDirty("comments");
							}}
						/>
					</div>

					{keptItems.length > 1 ? (
						<div className={styles.warning}>
							<div className={styles.warningHeader}>Warning! You have multiple listings selected.</div>
							<div className={styles.warningText}>Saving will overwrite the touched fields on every selected listing.</div>
						</div>
					) : null}
				</div>

				<div className={styles.right}>
					<div className={styles.rightTopHeaders}>
						<div className={styles.subHeader}>Changes Summary</div>
						<div className={styles.subHeaderSecondary}>Applying notes to {keptItems.length} items</div>
						{summaryMarketplaces.length ? (
							<div className={styles.summaryRow}>
								<span className={styles.summaryRowLabel}>Marketplace:</span>
								<span className={styles.summaryRowValue}>
									{summaryMarketplaces.join(", ")}
									{dirty.eventSite && chosenEventSite ? ` → ${chosenEventSite.label}` : ""}
								</span>
							</div>
						) : null}
						{fieldsToUpdate.length ? (
							<div className={styles.summaryRow}>
								<span className={styles.summaryRowLabel}>Fields to update:</span>
								<span className={styles.summaryRowValue}>{fieldsToUpdate.join(", ")}</span>
							</div>
						) : null}
						<div className={styles.summaryRow}>
							<span className={styles.summaryRowLabel}>Flags enabled:</span>
							{flagsEnabled.length ? (
								<span className={styles.summaryRowValuePositive}>{flagsEnabled.join(", ")}</span>
							) : (
								<span className={styles.summaryRowValue}>—</span>
							)}
						</div>
						<div className={styles.summaryRow}>
							<span className={styles.summaryRowLabel}>Flags disabled:</span>
							{flagsDisabled.length ? (
								<span className={styles.summaryRowValueDisabled}>{flagsDisabled.join(", ")}</span>
							) : (
								<span className={styles.summaryRowValue}>—</span>
							)}
						</div>
					</div>
					<div data-no-border="true" className={styles.singleListing}>
						<div className={clx(styles.secRowSeat, styles.headerSecRowSeat)}>
							<div className={clx(styles.itemTextPrimary, helperStyles.ellipsisDiv)}>Sec, Row, Seat</div>
						</div>
						<div className={styles.costLabel}>Cost</div>
						<div className={styles.xContainer}></div>
					</div>
					<div className={styles.listings}>{mappedItems}</div>
				</div>
			</div>
			<div className={styles.buttons}>
				<Button onClick={handleClose} variant={ButtonVariant.Secondary} disabled={saving}>
					Cancel
				</Button>
				<Button onClick={handleSave} isLoading={saving} disabled={!keptItems.length || saving}>
					Save changes
				</Button>
			</div>
		</div>
	);
};

export default BulkInternalNotes;
